import React, {useEffect, useState} from 'react';
import {Box, Stack, Card, FormControl, MenuItem, useTheme} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {LoadingButton} from '@mui/lab';
import {useGridApiRef, GridColDef} from '@mui/x-data-grid-premium';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';
import moment from 'moment';
import SelectInput from '@components/form/select-input/SelectInput';

import {useBoolean} from 'src/hooks/use-boolean';

import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import {GridCellRender} from '../../grid-utils';
import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import EmptyContent from '@components/empty-content';
import CustomPagination from '../../payment/table-custom-pagination/TableCustomPagination';
import {FormControlDiv} from '../../../module-search-filter/ModuleSearchFilterStyle';

import EncryptUtils from 'src/utils/EncryptUtils';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import {APP} from 'src/styles/variables';
import {rowStyles} from '@shared/utils/HelperUtils';
import {fCurrency} from 'src/utils/format-number';
import {getCurrentMonthStartDate, getCurrentMonthEndDate} from '../../util';

const PaymentReconciliation = (props: any) => {
  const gridApiRef = useGridApiRef();
  const reconciliationReport = useBoolean(true);
  const [formValues, setFormValues] = useState<any>({
    category: '',
    report_type: '',
    show_by: '',
    from_date: null,
    to_date: null,
    legacy_app: ''
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageCount, setPageCount] = useState(1);
  const [reconciliationReportRows, setReconciliationReportRows] = useState<any>(
    []
  );
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState<any>([
    25, 50, 100
  ]);
  const [totalRecords, setTotalRecords] = useState<any>(0);
  const [reportLoading, setReportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [legacyApps, setLegacyApps] = useState<any>([]);
  const theme = useTheme();
  const horizontalScroll = true;

  const isCtaDisabled =
    !formValues.category || !formValues.report_type || !formValues.legacy_app;
  const isExportDisabled =
    !formValues.category ||
    !formValues.report_type ||
    !formValues.legacy_app ||
    !reconciliationReportRows.length;

  const getRCMLegacyApps = async () => {
    if (!props?.paymentConfig?.rcm_sqls?.get_legacy_apps_sql) {
      return;
    }

    try {
      const tk = EncryptUtils.encryptedData({
        paymentConfig: props?.paymentConfig
      });
      const legacyAppData = await APIService.getRCMLegacyApps({tk});
      const legacyAppsData = legacyAppData?.data || [];
      setLegacyApps(legacyAppsData);
      if (legacyAppsData.length) {
        setFormValues((prev: any) => ({
          ...prev,
          legacy_app: legacyAppsData[0]?.LegacyApp
        }));
      }
    } catch (e) {
      console.error('Failed to load legacy apps', e);
    }
  };

  const init = () => {
    getRCMLegacyApps();
  };

  const getFormattedDate = (date: any) => {
    return date ? dayjs(date).startOf('day').format('YYYY-MM-DD HH:mm:ss') : '';
  };

  const getShowByLabel = () => {
    const showByDropdownOptions =
      props?.reportConfig?.fields?.show_by?.drop_down_options;
    const showByObj = showByDropdownOptions.find(
      (sbd: any) => sbd.value === formValues.show_by
    );
    return showByObj?.label || null;
  };

  const fields = props?.reportConfig?.fields;

  const getDateLabel = (label: string) => {
    const reportLabel = getShowByLabel();
    return reportLabel ? `${reportLabel} ${label}` : label;
  };

  const handleChange = (field: string, value: any) => {
    if (field === 'show_by' && !formValues.from_date && !formValues.to_date) {
      setFormValues((prev: any) => ({
        ...prev,
        from_date: getCurrentMonthStartDate(),
        to_date: getCurrentMonthEndDate()
      }));
    }
    setFormValues((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFilters = () => {
    if (!reconciliationReport.value) {
      reconciliationReport.onToggle();
    }
    setFormValues({
      category: '',
      report_type: '',
      show_by: '',
      from_date: null,
      to_date: null,
      legacy_app: ''
    });
    setReconciliationReportRows([]);
    setPage(1);
    setPageSize(25);
    setPageCount(1);
  };

  const getPostingLevel = () => {
    const legacyAppObj = legacyApps?.find(
      (l: any) => l.LegacyApp === formValues?.legacy_app
    );
    return legacyAppObj ? legacyAppObj?.PostingLevel : null;
  };

  const getPaymentReconciliationReport = async () => {
    const postingLevelValue = getPostingLevel();
    if (!postingLevelValue) return null;
    if (reconciliationReport.value) {
      reconciliationReport.onToggle();
    }
    const tk = EncryptUtils.encryptedData({
      category: formValues.category,
      reportType: formValues.report_type,
      showBy: formValues.show_by,
      legacyApp: formValues?.legacy_app,
      fromDate: getFormattedDate(formValues.from_date),
      toDate: getFormattedDate(formValues.to_date),
      limit: pageSize,
      offset: (page - 1) * pageSize,
      paymentConfig: props?.paymentConfig,
      postingLevel: postingLevelValue
    });
    try {
      setReportLoading(true);
      const reconciliationReportData =
        await APIService.getPaymentReconciliationReport({tk});
      setReconciliationReportRows(reconciliationReportData.data);
      setPageCount(reconciliationReportData.pageCount);
      setTotalRecords(reconciliationReportData.totalRecordsCount);
      setReportLoading(false);
    } catch (e) {
      setReportLoading(false);
    }
  };

  const exportPaymentReconciliationReport = async (exportType: string) => {
    const tk = EncryptUtils.encryptedData({
      category: formValues.category,
      reportType: formValues.report_type,
      showBy: formValues.show_by,
      legacyApp: formValues?.legacy_app,
      fromDate: getFormattedDate(formValues.from_date),
      toDate: getFormattedDate(formValues.to_date),
      exportType: exportType,
      paymentConfig: props?.paymentConfig,
      postingLevel: getPostingLevel()
    });
    try {
      setExportLoading(true);
      const reconciliationReportData =
        await APIService.exportPaymentReconciliationReport({tk});
      setExportLoading(false);
      const fileExtension = exportType === 'csv' ? 'csv' : 'xlsx';
      const filename = `Payment Reconciliation Report ${moment().format('YYYY-MM-DD hh:mm:ss')}.${fileExtension}`;
      const url = window.URL.createObjectURL(
        new Blob([reconciliationReportData.data])
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setExportLoading(false);
    }
  };

  const getLegacyAppOptions = () => {
    return legacyApps?.map((l: any) => {
      return {
        label: l?.LegacyApp,
        value: l?.LegacyApp
      };
    });
  };

  const getOptions = (dropdownFields: any, key: any) => {
    if (key === 'legacy_app') {
      return getLegacyAppOptions();
    } else if (key === 'report_type') {
      const postingLevel = getPostingLevel();
      if (postingLevel === 'INVOICE') {
        return dropdownFields[key].drop_down_options.filter((d: any) =>
          d.value.includes('invoice')
        );
      }
    }
    return dropdownFields[key].drop_down_options || [];
  };

  const getSelectOptionDisabledStatus = (key: any) => {
    if (['report_type', 'show_by'].includes(key)) {
      return formValues?.legacy_app ? false : true;
    }
    return false;
  };

  const isAdjReport = () => {
    return formValues?.category === 'adjustment_report';
  };

  const isPmtReport = () => {
    return formValues?.category === 'payment_report';
  };

  useEffect(() => {
    init();
    // const categoryDefaultVal = fields?.category?.drop_down_options?.[0]?.value;
    // setFormValues((prev: any) => ({
    //   ...prev,
    //   category: categoryDefaultVal
    // }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getPaymentReconciliationReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  let reconciliationReportColumns: GridColDef[] = [
    {
      field: 'BatchID',
      headerName: 'Batch ID',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'LegacyApp',
      headerName: 'Legacy App',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'BytepadCreatedDate',
      headerName: 'Batch Created On',
      type: 'date',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'CreatedByUserName',
      headerName: 'Batch Created By',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'BatchName',
      headerName: 'Batch Name',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'DepositDate',
      headerName: 'Deposit Date',
      type: 'date',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'TotalBatchAmount',
      headerName: 'Total Batch Amount',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'TotalBatchAmountPosted',
      headerName: 'Total Batch Amount Posted',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'TotalVarianceAmount',
      headerName: 'Total Variance',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'PostedDate',
      headerName: 'Batch Posted Date',
      type: 'date',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'PostingStatus',
      headerName: 'Batch Status',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'AccountNumber',
      headerName: 'Account Number',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'InvoiceNumber',
      headerName: 'Invoice Number',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'LastName',
      headerName: 'Patient Last Name',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'FirstName',
      headerName: 'Patient First Name',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'PaymentSource',
      headerName: 'Payment Source',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    isAdjReport()
      ? {
          field: 'AdjustmentCode',
          headerName: 'Adjustment Code',
          width: 100,
          headerAlign: 'left',
          align: 'left',
      sortable: true,
      filterable: true,
          renderCell: (params: any) => <GridCellRender value={params.value} />
        }
      : {
          field: 'PayCode',
          headerName: 'Pay Code',
          width: 100,
          headerAlign: 'left',
          align: 'left',
      sortable: true,
      filterable: true,
          renderCell: (params: any) => <GridCellRender value={params.value} />
        },
    {
      field: 'DefaultTracking',
      headerName: 'Tracking Description',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'ServiceDate',
      headerName: 'Service Date',
      type: 'date',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      field: 'CptCode',
      headerName: 'CPT4',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'RenderingPhysician',
      headerName: 'Rendering Provider',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      field: 'ChargeAmount',
      headerName: 'Billed Amount',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'Allowed',
      headerName: 'Allowed Amount',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    isAdjReport()
      ? {
          field: 'Adjustment',
          headerName: 'Adjustment Amount',
          width: 100,
          headerAlign: 'left',
          align: 'left',
      sortable: true,
      filterable: true,
          renderCell: (params: any) => (
            <GridCellRender value={fCurrency(params.value)} />
          )
        }
      : {
          field: 'Payment',
          headerName: 'Payment Amount',
          width: 100,
          headerAlign: 'left',
          align: 'left',
      sortable: true,
      filterable: true,
          renderCell: (params: any) => (
            <GridCellRender value={fCurrency(params.value)} />
          )
        }
    // {
    //   field: 'Balance',
    //   headerName: 'Balance Amount',
    //   width: 100,
    //   headerAlign: 'left',
    //   align: 'left',
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params: any) => (
    //     <GridCellRender value={fCurrency(params.value)} />
    //   )
    // },
  ];

  if (isPmtReport()) {
    reconciliationReportColumns = reconciliationReportColumns.concat([
      {
        field: 'CollectionAgencyPayment',
        headerName: 'Collection Agency Amount',
        width: 100,
        headerAlign: 'left',
        align: 'left',
      sortable: true,
      filterable: true,
        renderCell: (params: any) => (
          <GridCellRender value={fCurrency(params.value)} />
        )
      },
      {
        field: 'CollectionAgencyFee',
        headerName: 'Collection Agency Fee',
        width: 100,
        headerAlign: 'left',
        align: 'left',
      sortable: true,
      filterable: true,
        renderCell: (params: any) => (
          <GridCellRender value={fCurrency(params.value)} />
        )
      },
      {
        field: 'Reconciliation',
        headerName: 'Reconciliation',
        width: 100,
        headerAlign: 'left',
        align: 'left',
      sortable: true,
      filterable: true,
        renderCell: (params: any) => <GridCellRender value={params.value} />
      }
    ]);
  }

  if (getPostingLevel() === 'INVOICE') {
    reconciliationReportColumns = reconciliationReportColumns.filter(
      (rc: any) => rc.field !== 'CptCode'
    );
  }

  return (
    <Stack mt={2}>
      <Box px={3} mt={1}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <Box
            rowGap={3}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(3, 1fr)'
            }}>
            {Object.entries(fields)
              .sort((a: any, b: any) => a[1].order - b[1].order)
              .map(([key, field]: any) => (
                <Box key={key}>
                  {field.type === 'select' && (
                    <FormControl fullWidth>
                      <FormControlDiv key={key}>
                        <SelectInput
                          name={key}
                          disabled={getSelectOptionDisabledStatus(key)}
                          enableDefaultOption
                          options={getOptions(fields, key)}
                          label={fields[key]?.heading[0]}
                          placeholder={fields[key]?.placeholder[0]}
                          value={formValues[key] || ''}
                          fullWidth
                          onChange={(e: any) =>
                            handleChange(key, e.target.value)
                          }
                          optionValueKey={'value'}
                          optionLabelKey={'label'}
                        />
                      </FormControlDiv>
                    </FormControl>
                  )}

                  {field.type === 'date' && (
                    <DatePicker
                      label={getDateLabel(field.heading[0])}
                      value={formValues[key] ? formValues[key] : null}
                      onChange={date => handleChange(key, date)}
                      slotProps={{
                        textField: {
                          fullWidth: true
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0000003b',
                          borderWidth: '1.8px'
                        }
                      }}
                    />
                  )}
                </Box>
              ))}
          </Box>
          <Box
            sx={{
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}>
            <LoadingButton
              type="reset"
              variant="outlined"
              onClick={resetFilters}
              sx={{
                marginLeft: '12px',
                backgroundColor: theme.palette.primary.light
              }}>
              Reset
            </LoadingButton>
            <LoadingButton
              onClick={getPaymentReconciliationReport}
              loading={reportLoading}
              type="submit"
              variant="contained"
              disabled={isCtaDisabled}
              sx={{
                marginLeft: '12px',
                backgroundColor: theme.palette.primary.dark
              }}>
              Search
            </LoadingButton>
          </Box>
        </Card>
      </Box>
      <Box px={3} mt={1}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <DropDownPanel
            title="Reconciliation Report"
            collapse={reconciliationReport.value}
            onCollapse={reconciliationReport.onToggle}
            secondaryTitle={
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <LoadingButton
                  disabled={isExportDisabled}
                  type="submit"
                  variant="outlined"
                  loading={exportLoading}
                  sx={{
                    backgroundColor: theme.palette.primary.light
                  }}
                  onClick={() => exportPaymentReconciliationReport('csv')}
                  loadingPosition="start">
                  CSV Export
                </LoadingButton>
                <LoadingButton
                  disabled={isExportDisabled}
                  type="submit"
                  variant="contained"
                  loading={exportLoading}
                  onClick={() => exportPaymentReconciliationReport('excel')}
                  sx={{
                    marginLeft: '12px',
                    backgroundColor: theme.palette.primary.dark
                  }}
                  loadingPosition="start">
                  XLSX Export
                </LoadingButton>
              </Box>
            }>
            <Box
              px={3}
              sx={{
                marginTop: '1rem',
                marginBottom: '1rem',
                maxHeight: 'auto'
              }}>
              <StripedDataGrid
                showColumnVerticalBorder
                showCellVerticalBorder
                autoHeight
                disableRowGrouping
                disableRowSelectionOnClick
                disableColumnSelector
                disableAggregation
                // checkboxSelection={gridCheckboxSelection}
                apiRef={gridApiRef}
                loading={reportLoading}
                columns={reconciliationReportColumns}
                rows={reconciliationReportRows}
                rowThreshold={0}
                rowHeight={37}
                columnHeaderHeight={42}
                hideFooter={false}
                pagination={true}
                paginationMode="server"
                sortingMode="client"
                sortingOrder={['desc', 'asc']}
                filterMode="client"
                getRowId={(row: any) => row.id || row.ID || row.BatchId}
                onProcessRowUpdateError={(error: any) => {
                  return true;
                }}
                slotProps={{
                  filterPanel: {
                    sx: {'& .MuiDataGrid-filterFormValueInput': {width: 'auto'}}
                  }
                }}
                getRowClassName={(params: any) => {
                  return rowStyles(params);
                }}
                sx={{
                  backgroundColor: `${APP.PALETTE.COMMON.WHITE}`,
                  '& .MuiTablePagination-displayedRows': {
                    display: 'none'
                  },
                  '& .MuiPagination-ul': {
                    flexWrap: 'nowrap'
                  },
                  '& .MuiDataGrid-row': {
                    border: '1px solid lightgray',
                    borderTopWidth: '0px',
                    width: 'calc(100% - 2px)'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    border: '1px solid lightgray',
                    backgroundColor: `${theme.palette.grey[300]}`,
                    borderBottomColor: `${theme.palette.primary.lighter}`,
                    borderBottomWidth: '1px'
                  },
                  '& .MuiDataGrid-columnHeader--moving': {
                    backgroundColor: `${APP.PALETTE.DATA_GRID.HEADER}`
                  },
                  '& .AbnormalFlagText': {
                    color: `${APP.PALETTE.ABNORMAL_FLAG.TEXT}`,
                    fontWeight: 'bold !important'
                  },
                  '& .css-87hgir-MuiTypography-root': {
                    fontWeight: 'unset'
                  },
                  '& .css-11d09j8-MuiButtonBase-root-MuiCheckbox-root.Mui-checked':
                    {
                      color:
                        props?.checkboxColor ||
                        `${theme.palette.primary.lighter}`
                    },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '70px',
                    scrollBehavior: 'smooth',
                    overflow: horizontalScroll ? 'auto' : 'visible !important'
                  },
                  '& .MuiDataGrid-footerContainer': {
                    marginBottom: '1rem'
                  },
                  '& .MuiDataGrid-detailPanel': {
                    border: '1px solid lightgray'
                  },
                  '& .MuiDataGrid-pinnedRowsRenderZone': {
                    fontWeight: 'bolder'
                  },
                  '.autopost-grid-editable-cell': {
                    backgroundColor: '#e0f7fa',
                    color: '#004d40'
                  },
                  px: 3,
                  pr: 0,
                  pl: 0
                }}
                slots={{
                  detailPanelExpandIcon: ExpandMoreOutlined,
                  detailPanelCollapseIcon: ExpandLessOutlined,
                  pagination: () => (
                    <CustomPagination
                      page={page}
                      pageSize={pageSize}
                      rowsPerPageOptions={rowsPerPageOptions}
                      pageCount={pageCount}
                      totalRecordsCount={totalRecords}
                      totalRows={reconciliationReportRows.length}
                      setPage={setPage}
                      setPageSizes={setPageSize}
                    />
                  ),
                  noRowsOverlay: () => (
                    <EmptyContent
                      sx={{
                        py: 10
                      }}
                      title="No Data"
                    />
                  ),
                  noResultsOverlay: () => (
                    <EmptyContent
                      sx={{
                        py: 10
                      }}
                      title="No results found"
                    />
                  )
                }}
              />
            </Box>
          </DropDownPanel>
        </Card>
      </Box>
    </Stack>
  );
};

export default PaymentReconciliation;
